/*****************************************************
Naam: CustomerAdressenPanel.jsx
Locatie: src/components/CustomerPortal/CustomerAdressenPanel.jsx
Datum: 2026-01-25
Versie: 1.0.0
Auteur: Claude
******************************************************
BESCHRIJVING:
Panel voor laad- en losadressen in het klantenportal.
Gebaseerd op dashboard AdressenPanel maar aangepast voor klanten.

FEATURES:
- Laad en los adres invoer
- Datum selectie
- Land dropdown
- Bijzonderheden textarea
- Klant adres kopiëren (als beschikbaar)
- Zelfde styling als dashboard

Props:
- formData: Object met alle adres velden
- landen: Array met landen
- customerAddress: Object met klant adresgegevens (optioneel)
- onFormDataChange: (updates) => void
******************************************************/

import React from 'react';
import { MapPin, Calendar } from 'lucide-react';

const CustomerAdressenPanel = ({ 
  formData,
  landen = [],
  customerAddress = null,
  losAdressen = [],
  onFormDataChange
}) => {
  
  const handleKlantAdresKopieer = () => {
    if (customerAddress) {
      onFormDataChange({
        laadAdres: customerAddress.adres || '',
        laadPostcode: customerAddress.postcode || '',
        laadPlaats: customerAddress.plaats || '',
        laadLand: customerAddress.land || ''
      });
    }
  };

  const handleLosAdresSelect = (e) => {
    const key = e.target.value;
    if (!key || key === '__nieuw__') return;
    const adres = losAdressen.find(a => `${a.LosAdres}|${a.LosPostcode}|${a.LosPlaats}|${a.LosLand}` === key);
    if (adres) {
      onFormDataChange({
        losAdres: adres.LosAdres || '',
        losPostcode: adres.LosPostcode || '',
        losPlaats: adres.LosPlaats || '',
        losLand: adres.LosLand || ''
      });
    }
  };

  const handleInputChange = (field, value) => {
    onFormDataChange({ [field]: value });
  };

  // Minimum datum is vandaag
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="border-2 border-gray-300 p-5 bg-gray-50">
      <h3 className="font-bold text-lg mb-4 text-gray-800 flex items-center gap-2">
        <MapPin className="w-5 h-5 text-blue-900" />
        Adresgegevens
      </h3>
      
      {/* Rij-voor-rij grid zodat laad en los velden altijd op dezelfde hoogte staan */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0, border:'1px solid #e5e7eb', background:'white' }}>

        {/* ── Headers ── */}
        <div style={{ background:'#1a364c', borderBottom:'2px solid #d4a848', borderRight:'1px solid #0f2233', padding:'12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <h4 className="font-semibold flex items-center gap-2" style={{ color:'white' }}>
            <div className="w-3 h-3 rounded-full" style={{ background:'#d4a848' }}></div>
            Laadadres
          </h4>
          {customerAddress && (
            <label className="flex items-center gap-2 text-sm cursor-pointer px-2 py-1">
              <input type="checkbox" onChange={(e) => e.target.checked && handleKlantAdresKopieer()} className="w-4 h-4" />
              <span style={{ color:'#d4a848', fontWeight:500 }}>= Mijn adres</span>
            </label>
          )}
        </div>
        <div style={{ background:'#c2e6ef', borderBottom:'2px solid #1a364c', padding:'12px 16px', display:'flex', alignItems:'center' }}>
          <h4 className="font-semibold flex items-center gap-2" style={{ color:'#1a364c' }}>
            <div className="w-3 h-3 rounded-full" style={{ background:'#1a364c' }}></div>
            Losadres
          </h4>
        </div>

        {/* ── Eerder gebruikt (los) / lege ruimte (laad) ── */}
        <div style={{ borderBottom:'1px solid #f1f5f9', borderRight:'1px solid #e5e7eb', padding:'12px 16px' }}>
          {/* lege placeholder zodat de rijen uitlijnen */}
          <div style={{ height: losAdressen.length > 0 ? 44 : 0 }} />
        </div>
        <div style={{ borderBottom:'1px solid #f1f5f9', padding:'12px 16px' }}>
          {losAdressen.length > 0 && (
            <select onChange={handleLosAdresSelect} defaultValue=""
              className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 bg-white">
              <option value="">-- Eerder gebruikt adres --</option>
              {losAdressen.map((adres, idx) => {
                const display = [adres.LosAdres, adres.LosPostcode, adres.LosPlaats].filter(Boolean).join(', ');
                const key = `${adres.LosAdres}|${adres.LosPostcode}|${adres.LosPlaats}|${adres.LosLand}`;
                return <option key={idx} value={key}>{display}</option>;
              })}
            </select>
          )}
        </div>

        {/* ── Adres ── */}
        <div style={{ borderBottom:'1px solid #f1f5f9', borderRight:'1px solid #e5e7eb', padding:'12px 16px' }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
          <input type="text" value={formData.laadAdres} onChange={(e) => handleInputChange('laadAdres', e.target.value)}
            placeholder="Straatnaam + huisnummer"
            className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 transition-all" />
        </div>
        <div style={{ borderBottom:'1px solid #f1f5f9', padding:'12px 16px' }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
          <input type="text" value={formData.losAdres} onChange={(e) => handleInputChange('losAdres', e.target.value)}
            placeholder="Straatnaam + huisnummer"
            className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 transition-all" />
        </div>

        {/* ── Datum ── */}
        <div style={{ borderBottom:'1px solid #f1f5f9', borderRight:'1px solid #e5e7eb', padding:'12px 16px' }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Laaddatum</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="date" value={formData.laadDatum} onChange={(e) => handleInputChange('laadDatum', e.target.value)} min={today}
              className="w-full pl-10 pr-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 transition-all" />
          </div>
        </div>
        <div style={{ borderBottom:'1px solid #f1f5f9', padding:'12px 16px' }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Losdatum</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="date" value={formData.losDatum} onChange={(e) => handleInputChange('losDatum', e.target.value)} min={formData.laadDatum || today}
              className="w-full pl-10 pr-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 transition-all" />
          </div>
        </div>

        {/* ── Postcode + Plaats ── */}
        <div style={{ borderBottom:'1px solid #f1f5f9', borderRight:'1px solid #e5e7eb', padding:'12px 16px' }}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
              <input type="text" value={formData.laadPostcode} onChange={(e) => handleInputChange('laadPostcode', e.target.value)}
                placeholder="1234 AB" className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plaats <span className="text-red-500">*</span></label>
              <input type="text" value={formData.laadPlaats} onChange={(e) => handleInputChange('laadPlaats', e.target.value)}
                placeholder="Amsterdam" className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 transition-all" />
            </div>
          </div>
        </div>
        <div style={{ borderBottom:'1px solid #f1f5f9', padding:'12px 16px' }}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
              <input type="text" value={formData.losPostcode} onChange={(e) => handleInputChange('losPostcode', e.target.value)}
                placeholder="1234 AB" className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 transition-all" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plaats <span className="text-red-500">*</span></label>
              <input type="text" value={formData.losPlaats} onChange={(e) => handleInputChange('losPlaats', e.target.value)}
                placeholder="Rotterdam" className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 transition-all" />
            </div>
          </div>
        </div>

        {/* ── Land ── */}
        <div style={{ borderBottom:'1px solid #f1f5f9', borderRight:'1px solid #e5e7eb', padding:'12px 16px' }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Land <span className="text-red-500">*</span></label>
          <select value={formData.laadLand} onChange={(e) => handleInputChange('laadLand', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 bg-white">
            <option value="">-- Selecteer land --</option>
            {landen.map((land, idx) => <option key={idx} value={land}>{land}</option>)}
          </select>
        </div>
        <div style={{ borderBottom:'1px solid #f1f5f9', padding:'12px 16px' }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Land <span className="text-red-500">*</span></label>
          <select value={formData.losLand} onChange={(e) => handleInputChange('losLand', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 bg-white">
            <option value="">-- Selecteer land --</option>
            {landen.map((land, idx) => <option key={idx} value={land}>{land}</option>)}
          </select>
        </div>

        {/* ── Bijzonderheden ── */}
        <div style={{ borderRight:'1px solid #e5e7eb', padding:'12px 16px' }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bijzonderheden laden</label>
          <textarea value={formData.laadBijzonderheden} onChange={(e) => handleInputChange('laadBijzonderheden', e.target.value)}
            rows="2" placeholder="Bijv. bellen voor aankomst, laadtijden..."
            className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 resize-none" />
        </div>
        <div style={{ padding:'12px 16px' }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bijzonderheden lossen</label>
          <textarea value={formData.losBijzonderheden} onChange={(e) => handleInputChange('losBijzonderheden', e.target.value)}
            rows="2" placeholder="Bijv. lostijden, klep nodig..."
            className="w-full px-3 py-2.5 border border-gray-300 focus:ring-2 focus:ring-blue-300 resize-none" />
        </div>
      </div>
    </div>
  );
};

export default CustomerAdressenPanel;
